import React, { useRef, useState } from 'react';
import { Upload, FileText, Loader2, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentUpload = ({ 
    label,
    currentDocument, 
    token, 
    onDocumentUpdate,
    endpoint = '/api/athletes/upload-document',
    acceptedTypes = '.pdf,.jpg,.jpeg,.png',
    maxSizeMB = 25,
    disabled = false,
    testId = 'document-upload'
}) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error(`File must be less than ${maxSizeMB}MB`);
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(
                `${API_URL}${endpoint}`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            if (response.data.url) {
                onDocumentUpdate(response.data.url);
                toast.success(`${label} uploaded successfully!`);
            }
        } catch (error) {
            console.error('Document upload failed:', error);
            toast.error(`Failed to upload ${label.toLowerCase()}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground">
                {label}
            </Label>
            <div className="flex items-center gap-4">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedTypes}
                    onChange={handleUpload}
                    className="hidden"
                    disabled={disabled}
                    data-testid={`${testId}-input`}
                />
                
                {currentDocument ? (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 flex-1">
                        <Check className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400">Document uploaded</span>
                        <a 
                            href={currentDocument} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline ml-auto"
                        >
                            View
                        </a>
                        {!disabled && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => onDocumentUpdate('')}
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
                            >
                                <X className="w-3 h-3" />
                            </Button>
                        )}
                    </div>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || disabled}
                        className="border-white/10 bg-[#0a1628] flex-1 h-11 justify-start"
                        data-testid={`${testId}-btn`}
                    >
                        {uploading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploading ? 'Uploading...' : `Upload ${label}`}
                    </Button>
                )}
            </div>
            <p className="text-xs text-muted-foreground">
                Accepted: {acceptedTypes.replace(/\./g, '').toUpperCase()}. Max {maxSizeMB}MB
            </p>
        </div>
    );
};

export default DocumentUpload;
